import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  ContactInfo,
  ListingContactsResponse,
  ShareDraftRequest,
} from '../types';
import { ShareDraftContactCard } from './ShareDraftContactCard';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useService } from '../../../../../contexts/ServiceContext';
import { useFeatureFlags } from '../../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../../flags';
import { formatAddressLineOne } from '../../../../../system/location';

interface ShareDraftPanelProps {
  listingData: {
    listingKey: string;
    mlsId: string;
    address: {
      streetNumber?: string;
      streetDirPrefix?: string;
      streetName?: string;
      streetSuffix?: string;
      streetSuffixModifier?: string;
      streetDirSuffix?: string;
      unitNumber?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
  contacts: ListingContactsResponse['contacts'];
  onClose: () => void;
}

export const ShareDraftPanel: React.FC<ShareDraftPanelProps> = ({
  listingData,
  contacts,
  onClose,
}) => {
  const { hasFeature } = useFeatureFlags();
  const { sendShareDraft } = useService();
  const { session } = useAuth();

  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Check if the user is enrolled in the V2 share draft mode
  const isShareDraftV2 = hasFeature(FeatureFlags.NGW_SHARE_DRAFT_V2);

  // Get sender information from auth session
  const senderInfo = session?.user?.agent
    ? {
        name:
          session.user.agent.MemberFullName ||
          `${session.user.agent.MemberFirstName || ''} ${session.user.agent.MemberLastName || ''}`.trim() ||
          undefined,
        email: session.user.agent.MemberEmail || undefined,
        phone:
          session.user.agent.MemberPreferredPhone ||
          session.user.agent.MemberDirectPhone ||
          session.user.agent.MemberOfficePhone ||
          undefined,
        mlsId: session.user.agent.MemberMlsId || undefined,
        key: session.user.agent.MemberKey || undefined, // Add member key for OTP records
      }
    : {
        name: 'Unknown Agent',
      };

  // Format address
  const addressLine1 = formatAddressLineOne({
    streetNumber: listingData.address.streetNumber,
    streetDirPrefix: listingData.address.streetDirPrefix,
    streetName: listingData.address.streetName,
    streetSuffix: listingData.address.streetSuffix,
    streetModifier: listingData.address.streetSuffixModifier,
    streetDirSuffix: listingData.address.streetDirSuffix,
    unitNumber: listingData.address.unitNumber,
  });

  const addressLine2 = `${listingData.address.city}, ${listingData.address.state}`;

  // Get all contacts combined and deduplicated
  const allContacts: ContactInfo[] = contacts?.all || [];

  // Check if all contacts are selected
  const allSelected =
    allContacts.length > 0 &&
    allContacts.every((contact) => selectedContacts.has(contact.key));

  // Check if some but not all are selected
  const someSelected =
    allContacts.some((contact) => selectedContacts.has(contact.key)) &&
    !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      setSelectedContacts(new Set());
    } else {
      // Select all
      const allKeys = allContacts.map((contact) => contact.key);
      setSelectedContacts(new Set(allKeys));
    }
  };

  const handleToggleContact = (contactKey: string) => {
    const newSelection = new Set(selectedContacts);
    if (newSelection.has(contactKey)) {
      newSelection.delete(contactKey);
    } else {
      newSelection.add(contactKey);
    }
    setSelectedContacts(newSelection);
  };

  const handleSend = async () => {
    if (selectedContacts.size === 0 || !senderInfo) return;

    setIsSending(true);
    setError(null);

    try {
      // Get selected contact objects
      const recipientsToSend = allContacts.filter((contact) =>
        selectedContacts.has(contact.key),
      );

      // Prepare the request for the backend
      const shareRequest: ShareDraftRequest = {
        listingKey: listingData.listingKey,
        recipients: recipientsToSend.map((recipient) => ({
          email: recipient.email!,
          name: recipient.name || undefined,
          type: recipient.type,
          phone: recipient.phone || undefined,
          isOwner: recipient.isOwner,
          ownerNumber: recipient.ownerNumber,
        })),
        sender: senderInfo,
      };

      // Let share contact know this is a V2 share request
      if (isShareDraftV2) {
        shareRequest.mode = 'v2';
      }

      // Send via the backend endpoint
      const response = await sendShareDraft(shareRequest);

      if (response.success) {
        // Show success message
        setSuccessMessage(
          `Successfully sent to ${response.sentCount} recipient${
            response.sentCount > 1 ? 's' : ''
          }`,
        );

        setShowSuccess(true);

        // Clear selections after successful send
        setSelectedContacts(new Set());

        // Auto-hide success after 10 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 10000);

        // Show any partial errors
        if (response.errors && response.errors.length > 0) {
          console.warn('Some recipients failed:', response.errors);
        }
      } else {
        setError(response.message || 'Failed to send emails');
        if (response.errors && response.errors.length > 0) {
          setError(
            `${response.message}. Errors: ${response.errors.join(', ')}`,
          );
        }
      }
    } catch (err) {
      console.error('Failed to send share draft emails:', err);
      setError('Failed to send emails. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', fontWeight: 500 }}
        >
          MLS# {listingData.mlsId} - {addressLine1}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {addressLine2}
        </Typography>
      </Box>

      {/* Success Toast */}
      {showSuccess && (
        <Alert
          severity="success"
          sx={{ m: 2, mb: 0 }}
          onClose={() => setShowSuccess(false)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          sx={{ m: 2, mb: 0 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Select All */}
      <Box sx={{ p: 2, pb: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={handleSelectAll}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            sx={{
              color: 'text.secondary',
              '&.Mui-checked': { color: 'primary.main' },
              '&.MuiCheckbox-indeterminate': { color: 'primary.main' },
            }}
          />
          <Typography sx={{ color: 'text.primary', ml: 1 }}>
            Select All
          </Typography>
        </Box>
      </Box>

      {/* Contact List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, pt: 1 }}>
        {allContacts.map((contact) => (
          <ShareDraftContactCard
            key={contact.key}
            contact={contact}
            isSelected={selectedContacts.has(contact.key)}
            onToggle={() => handleToggleContact(contact.key)}
          />
        ))}

        {allContacts.length === 0 && (
          <Typography
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mt: 4,
            }}
          >
            No contacts available for this listing
          </Typography>
        )}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          bgcolor: 'grey.50',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: 'secondary.main',
            borderColor: 'secondary.main',
            '&:hover': {
              borderColor: 'secondary.dark',
              backgroundColor: (theme) => `${theme.palette.secondary.main}08`,
            },
          }}
        >
          CANCEL
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={selectedContacts.size === 0 || isSending || !senderInfo}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '&:disabled': {
              backgroundColor: 'grey.300',
              color: 'grey.500',
            },
            minWidth: 100,
          }}
        >
          {isSending ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'SEND'
          )}
        </Button>
      </Box>
    </Box>
  );
};
